import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Briefcase, MapPin, Building2, Mail, Phone, Euro, Plus, Lock, ArrowRight, X, Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useCampusAuth } from "@/contexts/CampusAuth";
import { formatApiErrorDetail } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/jobs`;

export default function CampusJobs() {
  const { user } = useCampusAuth();
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const isPoster = user?.tier === "Hauptquartier" || user?.is_admin;

  const load = async () => {
    try {
      const [j, c] = await Promise.all([
        axios.get(API, { withCredentials: true }),
        axios.get(`${API}/categories`),
      ]);
      setJobs(j.data);
      setCategories(c.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Stellenanzeigen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [j, c] = await Promise.all([
          axios.get(API, { withCredentials: true }),
          axios.get(`${API}/categories`),
        ]);
        if (!active) return;
        setJobs(j.data);
        setCategories(c.data);
      } catch (e) {
        if (active) toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Stellenanzeigen konnten nicht geladen werden.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  return (
    <div className="space-y-10">
      <header className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[12px] uppercase tracking-[0.28em] font-medium text-sand-500 mb-3">
            Helden-Mitarbeitersuche  ·  {jobs.length} offene Stellen
          </div>
          <h1 className="font-display text-sand-800 text-3xl sm:text-[2.4rem] font-semibold leading-[1.1]">
            Der Absolventen-Pool.
          </h1>
          <p className="mt-4 text-sand-600 text-[15.5px] leading-[1.65] max-w-2xl">
            Träger schreiben hier offene Stellen aus. Absolventen unseres Bildungszentrums (LG1, LG2, Praxisanleiter, PDLs)
            sehen die Anzeigen direkt im Campus.
            <strong className="text-sand-800"> Abwerbeschutz: einrichtungsübergreifendes Headhunting ist verboten.</strong>
          </p>
        </div>
        {isPoster ? (
          <Button
            onClick={() => setShowForm(true)}
            data-testid="jobs-new-cta"
            className="rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50 h-11 px-5 text-[13.5px] font-medium"
          >
            <span className="inline-flex items-center gap-2">
              <Plus className="h-4 w-4" strokeWidth={1.8} />
              Stelle ausschreiben
            </span>
          </Button>
        ) : (
          <Link
            to="/campus/team"
            data-testid="jobs-upgrade-cta"
            className="inline-flex items-center gap-2 rounded-full border border-terra text-terra hover:bg-terra hover:text-white transition-colors h-11 px-5 text-[13.5px] font-medium"
          >
            <Lock className="h-3.5 w-3.5" strokeWidth={1.8} />
            Mit Hauptquartier-Lizenz freischalten
          </Link>
        )}
      </header>

      {loading ? (
        <div className="text-sand-500 text-[14px]">Lade Stellenanzeigen …</div>
      ) : jobs.length === 0 ? (
        <EmptyState isPoster={isPoster} onNew={() => setShowForm(true)} />
      ) : (
        <section className="grid lg:grid-cols-2 gap-5">
          {jobs.map((j) => (
            <JobCard
              key={j.id}
              job={j}
              isOwn={j.posted_by_user_id === user?.id}
              onDelete={async () => {
                if (!window.confirm("Anzeige wirklich zurückziehen?")) return;
                try {
                  await axios.delete(`${API}/${j.id}`, { withCredentials: true });
                  toast.success("Anzeige zurückgezogen.");
                  load();
                } catch (e) {
                  toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Löschen fehlgeschlagen.");
                }
              }}
            />
          ))}
        </section>
      )}

      {showForm && (
        <JobFormModal
          categories={categories}
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function JobCard({ job, isOwn, onDelete }) {
  return (
    <article
      data-testid={`job-card-${job.id}`}
      className="rounded-2xl border border-cream-300 bg-white p-6 flex flex-col"
    >
      <div className="flex items-start gap-4">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-terra/10 text-terra shrink-0">
          <Briefcase className="h-5 w-5" strokeWidth={1.6} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-terra">{job.category}</div>
          <h3 className="mt-1 font-display text-sand-800 text-[1.2rem] font-semibold leading-tight">{job.title}</h3>
          <div className="mt-2 flex items-center gap-3 text-[12.5px] text-sand-600 flex-wrap">
            <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" strokeWidth={1.7} />{job.location}</span>
            <span>·</span>
            <span>{job.employment_type}</span>
            {job.salary_range && (<><span>·</span><span className="inline-flex items-center gap-1"><Euro className="h-3.5 w-3.5" strokeWidth={1.7} />{job.salary_range}</span></>)}
          </div>
        </div>
        {isOwn && (
          <button
            onClick={onDelete}
            data-testid={`job-delete-${job.id}`}
            className="text-sand-400 hover:text-terra inline-flex h-8 w-8 items-center justify-center rounded-lg hover:bg-cream-100"
            aria-label="Anzeige zurückziehen"
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.7} />
          </button>
        )}
      </div>
      <p className="mt-4 text-[14px] text-sand-700 leading-relaxed whitespace-pre-line">{job.description}</p>
      <div className="mt-5 pt-5 border-t border-cream-200 flex items-center justify-between flex-wrap gap-3">
        <div className="text-[12px] text-sand-500">
          {job.organisation && <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" strokeWidth={1.7} />{job.organisation}</span>}
        </div>
        <div className="flex items-center gap-3 text-[13px]">
          <a href={`mailto:${job.contact_email}`} className="inline-flex items-center gap-1.5 text-terra hover:underline font-medium">
            <Mail className="h-3.5 w-3.5" strokeWidth={1.8} />Bewerben
          </a>
          {job.contact_phone && (
            <a href={`tel:${job.contact_phone}`} className="inline-flex items-center gap-1.5 text-sand-700 hover:text-sand-800">
              <Phone className="h-3.5 w-3.5" strokeWidth={1.8} />{job.contact_phone}
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

function EmptyState({ isPoster, onNew }) {
  return (
    <div className="rounded-3xl border border-dashed border-cream-300 bg-white p-12 text-center">
      <Briefcase className="h-8 w-8 text-sand-400 mx-auto" strokeWidth={1.4} />
      <h3 className="mt-4 font-display text-sand-800 text-xl font-semibold">Noch keine Stellen.</h3>
      <p className="mt-2 text-sand-600 text-[14px] max-w-md mx-auto">
        Sobald Träger im Hauptquartier offene Stellen ausschreiben, erscheinen sie hier.
      </p>
      {isPoster && (
        <Button onClick={onNew} className="mt-6 rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50 h-11 px-5">
          <span className="inline-flex items-center gap-2"><Plus className="h-4 w-4" strokeWidth={1.8} />Erste Stelle ausschreiben</span>
        </Button>
      )}
    </div>
  );
}

function JobFormModal({ categories, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", category: categories[0] || "", location: "", employment_type: "Vollzeit",
    description: "", salary_range: "", contact_email: "", contact_phone: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await axios.post(API, form, { withCredentials: true });
      toast.success("Anzeige veröffentlicht.");
      onCreated();
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Veröffentlichen fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-sand-800/30 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-2xl rounded-2xl bg-white border border-cream-300 shadow-hover p-7 md:p-8 overflow-y-auto max-h-[90vh]">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 h-9 w-9 inline-flex items-center justify-center rounded-full text-sand-500 hover:bg-cream-100"
        >
          <X className="h-4 w-4" strokeWidth={1.7} />
        </button>
        <h2 className="font-display text-sand-800 text-2xl font-semibold">Neue Stellenanzeige</h2>
        <p className="mt-1 text-sand-600 text-[13.5px]">Sichtbar für alle Dickies-Campus-Mitglieder.</p>

        <form onSubmit={submit} className="mt-6 grid gap-4" data-testid="job-form">
          <Field label="Titel" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="job-form-title" required />
          <div className="grid sm:grid-cols-2 gap-4">
            <SelectField label="Kategorie" value={form.category} options={categories} onChange={(v) => setForm({ ...form, category: v })} testid="job-form-category" />
            <Field label="Beschäftigung" value={form.employment_type} onChange={(v) => setForm({ ...form, employment_type: v })} testid="job-form-employment" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Ort" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="job-form-location" required />
            <Field label="Gehalt (optional)" value={form.salary_range} onChange={(v) => setForm({ ...form, salary_range: v })} testid="job-form-salary" placeholder="z. B. 3.200–3.800 €" />
          </div>
          <TextAreaField label="Beschreibung" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="job-form-description" required />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Bewerbungs-E-Mail" type="email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testid="job-form-email" required />
            <Field label="Telefon (optional)" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} testid="job-form-phone" />
          </div>
          {err && <p className="text-[13px] text-terra font-medium" data-testid="job-form-error">{err}</p>}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="text-[13.5px] text-sand-600 hover:text-sand-800 underline-offset-4 hover:underline">
              Abbrechen
            </button>
            <Button type="submit" disabled={busy} data-testid="job-form-submit" className="rounded-full bg-sand-800 hover:bg-sand-700 text-cream-50 h-11 px-5 disabled:opacity-60">
              <span className="inline-flex items-center gap-2">{busy ? "Wird veröffentlicht…" : "Veröffentlichen"}{!busy && <ArrowRight className="h-4 w-4" strokeWidth={1.8} />}</span>
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", placeholder, required }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2 block">{label}</label>
      <input
        type={type} value={value} placeholder={placeholder} required={required}
        onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-xl border border-cream-300 bg-white px-4 h-11 text-[14.5px] outline-none focus:border-sand-800 transition-colors"
      />
    </div>
  );
}
function SelectField({ label, value, options, onChange, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2 block">{label}</label>
      <select
        value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-xl border border-cream-300 bg-white px-3 h-11 text-[14.5px] outline-none focus:border-sand-800"
      >
        {options.map((o) => (<option key={o} value={o}>{o}</option>))}
      </select>
    </div>
  );
}
function TextAreaField({ label, value, onChange, testid, required }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.22em] font-semibold text-sand-500 mb-2 block">{label}</label>
      <textarea
        value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} required={required} rows={5}
        className="w-full rounded-xl border border-cream-300 bg-white px-4 py-3 text-[14.5px] outline-none focus:border-sand-800 transition-colors resize-y"
      />
    </div>
  );
}
